import { useEffect, useRef, useCallback, useState } from 'react';
import { CustomNode } from '@/types/node-types';
import { CustomEdge } from '@/types/edge-types';
import HashWorker from '@/workers/hash-worker?worker';
import type { HashWorkerOutput } from '@/workers/hash-worker';
import { AutoSaveStatus } from '@/types/workflow-types';
import { isLoggingOut } from '@/utils/logout-state';
import { workflowContentKey } from '@/utils/workflow-content-key';

export interface UseAutoSaveOptions {
  nodes: CustomNode[];
  edges: CustomEdge[];
  workflowId: string | undefined;
  workflowName: string | undefined;
  workflowDescription: string | undefined;
  isNewWorkflow: boolean;
  isLoadingWorkflow: boolean;
  isLoadingTemplate: boolean;
  isOwner: boolean;
  onSave: () => Promise<void>;
  onForceSaveModal?: () => void; // For new workflows that need name/description
  debounceDelay?: number; // Default: 2000ms
  enabled?: boolean; // Default: true
}

export interface UseAutoSaveReturn {
  status: AutoSaveStatus;
  lastSavedTime: number | null;
  hasUnsavedChanges: boolean;
  forceSave: () => Promise<void>;
  resetBaseline: () => void;
}

/**
 * Custom hook for auto-saving workflow changes
 *
 * Features:
 * - Debounced auto-save after changes
 * - Browser beforeunload handling (warns on tab close)
 * - Change detection
 * - Keyboard shortcut support (Cmd/Ctrl+S)
 */
export function useAutoSave({
  nodes,
  edges,
  workflowId,
  workflowName,
  workflowDescription,
  isNewWorkflow,
  isLoadingWorkflow,
  isLoadingTemplate,
  isOwner,
  onSave,
  onForceSaveModal: _onForceSaveModal, // Reserved for future use - navigation blocking removed
  debounceDelay = 2000,
  enabled = true,
}: UseAutoSaveOptions): UseAutoSaveReturn {
  const [status, setStatus] = useState<AutoSaveStatus>(AutoSaveStatus.IDLE);
  const [lastSavedTime, setLastSavedTime] = useState<number | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Track the last saved state to detect changes
  const lastSavedStateRef = useRef<string>('');
  const lastSavedNodesRef = useRef<CustomNode[]>([]);
  const lastSavedEdgesRef = useRef<CustomEdge[]>([]);
  const lastSavedNameRef = useRef<string | undefined>(undefined);
  const lastSavedDescriptionRef = useRef<string | undefined>(undefined);
  // Store serialized node data for efficient comparison (detects prompt/parameter changes)
  const lastSavedNodeDataMapRef = useRef<Map<string, string>>(new Map());
  // Cache for current node data strings to avoid re-stringifying on every comparison
  // Maps node ID -> { dataRef: reference to node.data, dataStr: stringified version }
  const currentNodeDataCacheRef = useRef<
    Map<string, { dataRef: unknown; dataStr: string }>
  >(new Map());
  const isSavingRef = useRef(false);
  const hasInitializedRef = useRef(false);
  const hasCompletedLoadingRef = useRef(false);
  const previousNodeCountRef = useRef<number>(-1);
  const workerRef = useRef<Worker | null>(null);
  const pendingRequestIdRef = useRef<number>(0);
  const saveProcessTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingHashResolveRef = useRef<((hash: string) => void) | null>(null);
  const latestStateRef = useRef<{
    nodes: CustomNode[];
    edges: CustomEdge[];
    workflowName: string | undefined;
    workflowDescription: string | undefined;
  }>({ nodes, edges, workflowName, workflowDescription });

  // Initialize Web Worker
  useEffect(() => {
    // Create worker instance
    const worker = new HashWorker();
    workerRef.current = worker;

    // Handle messages from worker
    worker.onmessage = (e: MessageEvent<HashWorkerOutput>) => {
      const { hash, requestId, error } = e.data;

      // Only process if this is the latest request (ignore stale responses)
      if (requestId === pendingRequestIdRef.current) {
        if (error) {
          console.error('Hash worker computation error:', error);
          // Fallback to synchronous computation
          try {
            const {
              nodes: stateNodes,
              edges: stateEdges,
              workflowName: stateName,
              workflowDescription: stateDesc,
            } = latestStateRef.current;
            const fallbackHash = workflowContentKey({
              nodes: stateNodes,
              edges: stateEdges,
              workflowName: stateName,
              workflowDescription: stateDesc,
            });
            // Resolve pending hash promise
            if (pendingHashResolveRef.current) {
              pendingHashResolveRef.current(fallbackHash);
              pendingHashResolveRef.current = null;
            }
          } catch (fallbackError) {
            console.error('Fallback hash computation failed:', fallbackError);
            if (pendingHashResolveRef.current) {
              pendingHashResolveRef.current('');
              pendingHashResolveRef.current = null;
            }
          }
        } else {
          // Resolve pending hash promise
          if (pendingHashResolveRef.current) {
            pendingHashResolveRef.current(hash);
            pendingHashResolveRef.current = null;
          }
        }
      }
    };

    // Handle worker errors
    worker.onerror = (error) => {
      console.error('Hash worker error:', error);
      // Note: Fallback computation will be handled in the hash computation effect
      // if worker fails, we'll fall back to synchronous computation there
    };

    // Cleanup on unmount
    return () => {
      worker.terminate();
      workerRef.current = null;
      if (saveProcessTimeoutRef.current) {
        clearTimeout(saveProcessTimeoutRef.current);
      }
    };
  }, []); // Only run once on mount

  // Update latest state ref when dependencies change
  useEffect(() => {
    latestStateRef.current = {
      nodes,
      edges,
      workflowName,
      workflowDescription,
    };
  }, [nodes, edges, workflowName, workflowDescription]);

  // Combined initialization and change detection effect
  // This ensures atomic updates and prevents race conditions between initialization and comparison
  useEffect(() => {
    if (isLoadingWorkflow || isLoadingTemplate) {
      return;
    }

    // For existing workflows, initialize the baseline on first load
    // Wait for data to stabilize: node count must be the same for 2 consecutive renders
    // This ensures we don't initialize with partially loaded data
    const currentNodeCount = nodes.length;
    const nodeCountStable = previousNodeCountRef.current === currentNodeCount;
    previousNodeCountRef.current = currentNodeCount;

    if (
      !isNewWorkflow &&
      !hasInitializedRef.current &&
      hasCompletedLoadingRef.current &&
      workflowName && // Workflow metadata is loaded
      nodeCountStable // Node data has stabilized
    ) {
      // Initialize refs synchronously
      lastSavedNodesRef.current = [...nodes];
      lastSavedEdgesRef.current = [...edges];
      lastSavedNameRef.current = workflowName;
      lastSavedDescriptionRef.current = workflowDescription;
      lastSavedNodeDataMapRef.current = new Map(
        nodes.map((node) => [node.id, JSON.stringify(node.data)]),
      );
      hasInitializedRef.current = true;

      // No changes on initial load
      setHasUnsavedChanges(false);
      return;
    }

    // Skip change detection if not yet initialized or loading hasn't completed
    // This prevents false positives when the workflow is still loading
    if (!hasCompletedLoadingRef.current) {
      return;
    }

    if (!hasInitializedRef.current && !isNewWorkflow) {
      return;
    }

    // Compare nodes by ID (not by index) to avoid false positives when nodes are reordered
    // Build a map of saved nodes by ID for O(1) lookup
    const savedNodesMap = new Map(
      lastSavedNodesRef.current.map((node) => [node.id, node]),
    );

    // Helper to get stringified node data with caching to avoid expensive re-stringification
    const getNodeDataString = (nodeId: string, nodeData: unknown): string => {
      const cached = currentNodeDataCacheRef.current.get(nodeId);
      // If we have a cached entry and the data reference hasn't changed, reuse the string
      if (cached && cached.dataRef === nodeData) {
        return cached.dataStr;
      }
      // Otherwise, stringify and cache
      const dataStr = JSON.stringify(nodeData);
      currentNodeDataCacheRef.current.set(nodeId, {
        dataRef: nodeData,
        dataStr,
      });
      return dataStr;
    };

    // Check if nodes have changed: different count, missing nodes, or modified nodes
    const nodesChanged =
      nodes.length !== lastSavedNodesRef.current.length ||
      nodes.some((node) => {
        const savedNode = savedNodesMap.get(node.id);
        // Node doesn't exist in saved state (new node)
        if (!savedNode) return true;
        // Position changed
        if (node.position.x !== savedNode.position.x) return true;
        if (node.position.y !== savedNode.position.y) return true;
        // Compare node data (prompts, parameters, toggles, file uploads, etc.)
        // If the node's data isn't in the map, treat it as a change (conservative approach)
        const savedDataStr = lastSavedNodeDataMapRef.current.get(node.id);
        if (savedDataStr === undefined) return true;
        // Use cached stringification to avoid expensive re-stringify on every render
        const currentDataStr = getNodeDataString(node.id, node.data);
        if (currentDataStr !== savedDataStr) return true;
        return false;
      });

    // Compare edges by ID (not by index) to avoid false positives when edges are reordered
    const savedEdgesMap = new Map(
      lastSavedEdgesRef.current.map((edge) => [edge.id, edge]),
    );

    const edgesChanged =
      edges.length !== lastSavedEdgesRef.current.length ||
      edges.some((edge) => {
        const savedEdge = savedEdgesMap.get(edge.id);
        if (!savedEdge) return true;
        if (edge.source !== savedEdge.source) return true;
        if (edge.target !== savedEdge.target) return true;
        return false;
      });
    const nameChanged = workflowName !== lastSavedNameRef.current;
    const descriptionChanged =
      workflowDescription !== lastSavedDescriptionRef.current;

    const hasChanges =
      nodesChanged || edgesChanged || nameChanged || descriptionChanged;

    setHasUnsavedChanges(hasChanges);

    // Clean up cache entries for nodes that no longer exist to prevent memory leaks
    const currentNodeIds = new Set(nodes.map((n) => n.id));
    for (const cachedId of currentNodeDataCacheRef.current.keys()) {
      if (!currentNodeIds.has(cachedId)) {
        currentNodeDataCacheRef.current.delete(cachedId);
      }
    }
  }, [
    nodes,
    edges,
    workflowName,
    workflowDescription,
    isLoadingWorkflow,
    isLoadingTemplate,
    isNewWorkflow,
  ]);

  // Compute hash using Web Worker (returns a promise)
  const computeHash = useCallback((): Promise<string> => {
    return new Promise((resolve) => {
      // Fallback to synchronous computation if worker is not available
      if (!workerRef.current) {
        try {
          const {
            nodes: stateNodes,
            edges: stateEdges,
            workflowName: stateName,
            workflowDescription: stateDesc,
          } = latestStateRef.current;
          const hash = workflowContentKey({
            nodes: stateNodes,
            edges: stateEdges,
            workflowName: stateName,
            workflowDescription: stateDesc,
          });
          resolve(hash);
        } catch (error) {
          console.error('Hash computation failed:', error);
          resolve('');
        }
        return;
      }

      // Store resolve function to call when hash is ready
      pendingHashResolveRef.current = resolve;

      // Increment request ID to track latest request
      pendingRequestIdRef.current += 1;
      const currentRequestId = pendingRequestIdRef.current;

      // Get current state - send raw arrays directly to worker
      // postMessage will clone them automatically, and worker will do all transformation
      const {
        nodes: stateNodes,
        edges: stateEdges,
        workflowName: stateName,
        workflowDescription: stateDesc,
      } = latestStateRef.current;

      // Send raw arrays directly - worker will extract and transform
      // This avoids blocking the main thread with .map() operations
      workerRef.current.postMessage({
        nodes: stateNodes,
        edges: stateEdges,
        workflowName: stateName,
        workflowDescription: stateDesc,
        requestId: currentRequestId,
      });
    });
  }, []);

  // Auto-save function (computes hash and saves)
  const performSave = useCallback(async () => {
    // Skip if conditions aren't met
    if (
      !enabled ||
      isSavingRef.current ||
      isLoadingWorkflow ||
      isLoadingTemplate ||
      !isOwner ||
      isNewWorkflow // Don't auto-save new workflows until they have an ID
    ) {
      return;
    }

    isSavingRef.current = true;
    setStatus(AutoSaveStatus.SAVING);

    try {
      // Compute hash (this may block, but only happens after debounce)
      const currentHash = await computeHash();

      // Only save if there are actual changes
      if (currentHash === lastSavedStateRef.current) {
        isSavingRef.current = false;
        setStatus(AutoSaveStatus.IDLE);
        return;
      }

      // Perform the actual save
      await onSave();

      // Update saved state after successful save
      lastSavedStateRef.current = currentHash;
      lastSavedNodesRef.current = [...nodes];
      lastSavedEdgesRef.current = [...edges];
      lastSavedNameRef.current = workflowName;
      lastSavedDescriptionRef.current = workflowDescription;
      // Update serialized node data map for change detection
      lastSavedNodeDataMapRef.current = new Map(
        nodes.map((node) => [node.id, JSON.stringify(node.data)]),
      );
      setLastSavedTime(Date.now());
      setHasUnsavedChanges(false);
      setStatus(AutoSaveStatus.SAVED);

      // Clear saved status after 2 seconds
      setTimeout(() => {
        setStatus((prev) =>
          prev === AutoSaveStatus.SAVED ? AutoSaveStatus.IDLE : prev,
        );
      }, 2000);
    } catch (error) {
      console.error('Auto-save failed:', error);
      setStatus(AutoSaveStatus.ERROR);

      // Clear error status after 5 seconds
      setTimeout(() => {
        setStatus((prev) =>
          prev === AutoSaveStatus.ERROR ? AutoSaveStatus.IDLE : prev,
        );
      }, 5000);
    } finally {
      isSavingRef.current = false;
    }
  }, [
    enabled,
    isLoadingWorkflow,
    isLoadingTemplate,
    isOwner,
    isNewWorkflow,
    nodes,
    edges,
    workflowName,
    workflowDescription,
    computeHash,
    onSave,
  ]);

  // Single debounced save process (3000ms debounce at the START of save process)
  useEffect(() => {
    if (!enabled || isLoadingWorkflow || isLoadingTemplate) {
      return;
    }

    // Clear any pending timeout
    if (saveProcessTimeoutRef.current) {
      clearTimeout(saveProcessTimeoutRef.current);
    }

    // Debounce the entire save process (hash computation + save) by debounceDelay
    // This prevents blocking operations during rapid changes
    saveProcessTimeoutRef.current = setTimeout(() => {
      performSave();
    }, debounceDelay);

    return () => {
      if (saveProcessTimeoutRef.current) {
        clearTimeout(saveProcessTimeoutRef.current);
      }
    };
  }, [
    nodes,
    edges,
    workflowName,
    workflowDescription,
    enabled,
    isLoadingWorkflow,
    isLoadingTemplate,
    debounceDelay,
    performSave,
  ]);

  // Handle browser navigation (beforeunload)
  useEffect(() => {
    if (!enabled || !hasUnsavedChanges) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isLoggingOut()) {
        return;
      }

      if (isNewWorkflow) {
        // For new workflows, always show browser warning
        event.preventDefault();
        event.returnValue = '';
      } else if (hasUnsavedChanges) {
        // For existing workflows, show browser warning
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled, hasUnsavedChanges, isNewWorkflow]);

  // Handle visibility change (tab hidden)
  useEffect(() => {
    if (!enabled || !hasUnsavedChanges || isNewWorkflow) {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.hidden && hasUnsavedChanges && !isNewWorkflow) {
        // Save when tab becomes hidden (if not a new workflow)
        performSave();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, hasUnsavedChanges, isNewWorkflow, performSave]);

  // Reset initialization flag when workflow changes
  useEffect(() => {
    if (isLoadingWorkflow || isLoadingTemplate) {
      hasInitializedRef.current = false;
      hasCompletedLoadingRef.current = false;
      previousNodeCountRef.current = -1;
      lastSavedStateRef.current = '';
      lastSavedNodesRef.current = [];
      lastSavedEdgesRef.current = [];
      lastSavedNameRef.current = undefined;
      lastSavedDescriptionRef.current = undefined;
      lastSavedNodeDataMapRef.current = new Map();
      currentNodeDataCacheRef.current = new Map();
      setHasUnsavedChanges(false);
    } else {
      hasCompletedLoadingRef.current = true;
    }
  }, [isLoadingWorkflow, isLoadingTemplate, workflowId]);

  // Force save function (for manual saves)
  const forceSave = useCallback(async () => {
    if (isSavingRef.current) {
      return;
    }

    await performSave();
  }, [performSave]);

  // Reset baseline state after a successful manual save
  // This updates all refs to the current state so hasUnsavedChanges becomes false
  const resetBaseline = useCallback(() => {
    // Update refs synchronously for immediate change detection reset
    lastSavedNodesRef.current = [...nodes];
    lastSavedEdgesRef.current = [...edges];
    lastSavedNameRef.current = workflowName;
    lastSavedDescriptionRef.current = workflowDescription;
    lastSavedNodeDataMapRef.current = new Map(
      nodes.map((node) => [node.id, JSON.stringify(node.data)]),
    );
    setHasUnsavedChanges(false);

    // Compute hash asynchronously for auto-save baseline (non-blocking)
    computeHash().then((hash) => {
      lastSavedStateRef.current = hash;
    });
  }, [nodes, edges, workflowName, workflowDescription, computeHash]);

  return {
    status,
    lastSavedTime,
    hasUnsavedChanges,
    forceSave,
    resetBaseline,
  };
}
